/**
 * Status utility functions for crew management components
 */

export const STATUS_TYPES = {
  FULFILLED: "fulfilled",
  PARTIALLY_FULFILLED: "partially_fulfilled",
  NOT_FULFILLED: "not_fulfilled",
};

export const SUGGESTION_TYPES = {
  SUCCESS: "success",
  WARNING: "warning",
  INFO: "info",
};

/**
 * Get the appropriate chip color based on status and fulfillment percentage
 */
export function getChipColor(status, fulfillmentStatus = null) {
  switch (status) {
    case STATUS_TYPES.FULFILLED:
      return "green lighten-4 green--text text--darken-4";
    case STATUS_TYPES.PARTIALLY_FULFILLED:
      if (fulfillmentStatus && fulfillmentStatus.fulfillmentPercentage) {
        const percentage = fulfillmentStatus.fulfillmentPercentage;
        if (percentage >= 75) {
          return "light-green lighten-4 light-green--text text--darken-4";
        } else if (percentage >= 50) {
          return "orange lighten-4 orange--text text--darken-4";
        } else {
          return "red lighten-3 red--text text--darken-4";
        }
      }
      return "orange lighten-4 orange--text text--darken-4";
    case STATUS_TYPES.NOT_FULFILLED:
      return "red lighten-4 red--text text--darken-4";
    default:
      return "grey lighten-4 grey--text text--darken-4";
  }
}

/**
 * Get the appropriate icon for a given status
 */
export function getStatusIcon(status) {
  const iconMap = {
    [STATUS_TYPES.FULFILLED]: "mdi-check-circle",
    [STATUS_TYPES.PARTIALLY_FULFILLED]: "mdi-alert-circle",
    [STATUS_TYPES.NOT_FULFILLED]: "mdi-close-circle",
  };
  return iconMap[status] || "mdi-help-circle";
}

/**
 * Get the appropriate icon color for a given status
 */
export function getStatusIconColor(status) {
  const colorMap = {
    [STATUS_TYPES.FULFILLED]: "green darken-4",
    [STATUS_TYPES.PARTIALLY_FULFILLED]: "red darken-4",
    [STATUS_TYPES.NOT_FULFILLED]: "red darken-4",
  };
  return colorMap[status] || "grey darken-4";
}

/**
 * Get position-specific status icon
 */
export function getPositionStatusIcon(status) {
  const iconMap = {
    [STATUS_TYPES.FULFILLED]: "mdi-check-circle",
    [STATUS_TYPES.NOT_FULFILLED]: "mdi-close-circle",
  };
  return iconMap[status] || "mdi-help-circle";
}

/**
 * Get suggestion alert type for Vuetify alerts
 */
export function getSuggestionAlertType(type) {
  const typeMap = {
    [SUGGESTION_TYPES.SUCCESS]: "success",
    [SUGGESTION_TYPES.WARNING]: "warning",
    [SUGGESTION_TYPES.INFO]: "info",
  };
  return typeMap[type] || "info";
}

/**
 * Calculate progress color based on fulfillment percentage
 */
export function getProgressColor(fulfillmentStatus) {
  const percentage =
    (fulfillmentStatus.totalAvailable /
      Math.max(fulfillmentStatus.totalRequired, 1)) *
    100;
  const cappedPercentage = Math.min(percentage, 100);

  if (cappedPercentage >= 100) return "success";
  if (cappedPercentage >= 75) return "light-green";
  if (cappedPercentage >= 50) return "orange";
  if (cappedPercentage >= 25) return "deep-orange";
  return "red";
}

/**
 * Calculate progress background color
 */
export function getProgressBackgroundColor(fulfillmentStatus) {
  return fulfillmentStatus.remainingPersonnel > 0
    ? "blue lighten-4"
    : "grey lighten-4";
}

/**
 * Calculate progress value for progress bars
 */
export function getProgressValue(fulfillmentStatus) {
  const totalAvailable = fulfillmentStatus.totalAvailable;
  const totalRequired = Math.max(fulfillmentStatus.totalRequired, 1);
  const remainingPersonnel = fulfillmentStatus.remainingPersonnel || 0;

  if (remainingPersonnel > 0) {
    const totalPersonnel = totalAvailable + remainingPersonnel;
    return (totalRequired / Math.max(totalPersonnel, 1)) * 100;
  } else {
    return (totalAvailable / totalRequired) * 100;
  }
}

/**
 * Get priority color based on numeric priority value
 */
export function getPriorityColor(priority) {
  if (priority <= 2) return "red";
  if (priority <= 4) return "orange";
  return "green";
}

/**
 * Get suggestion color based on type
 */
export function getSuggestionColor(type) {
  const colorMap = {
    [SUGGESTION_TYPES.SUCCESS]: "green",
    [SUGGESTION_TYPES.WARNING]: "orange",
    [SUGGESTION_TYPES.INFO]: "blue",
  };
  return colorMap[type] || "grey";
}
